import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toPng } from 'html-to-image';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Stack,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Link,
  Collapse,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ArrowUpward as SendIcon,
  Add as AddIcon,
  Chat as ChatIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Lightbulb as LightbulbIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Source as SourceIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  Article as ArticleIcon,
  Warning as WarningIcon,
  Summarize as SummarizeIcon,
  Description as DescriptionIcon,
  UploadFile as UploadFileIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useMultiWebSocket } from '@/common-hooks/use-multi-websocket';
import { AI_CHAT_STORAGE_KEY, AI_CHAT_SELECTED_KEY } from '@/common-constants/storage-keys';
import type { ChatMessage, ChatConversation } from '@/common-types/models/chat';
import { minimizeOrgComparisonJson, buildOrgAnalysisPrompt } from './org-analysis-utils';
import type { OrgComparisonRaw, OrgSnapshotRaw } from './org-analysis-utils';
import {
  fetchJobSummaryPrompt,
  fetchTerraformErrors,
  fetchFlowErrors,
  minifyTerraformErrorsByResourceType,
} from './job-summary-utils';
import { transactionService } from '@/services/api';

// Constants

const MAX_CONVERSATIONS = 5;
const CHARS_PER_FRAME_BASE = 3;
const SKIP_ANIMATION_THRESHOLD = 5000;
const TITLE_PREVIEW_LENGTH = 50;
const DEDUP_WINDOW_MS = 1000;
const DEDUP_KEY_LENGTH = 50;
const SUMMARY_CMD_REGEX = /^\/summary\s+(\S+)\s*$/i;
const ORG_ANALYSIS_CMD_REGEX = /^\/org-analysis\s*$/i;
const SCROLL_DELAY_MS = 100;

interface SlashCommand {
  command: string;
  label: string;
  description: string;
  placeholder: string;
}

const SLASH_COMMANDS: SlashCommand[] = [
  {
    command: '/summary',
    label: '/summary',
    description: 'Summarize a BCP sync job by transaction ID',
    placeholder: '/summary <paste transaction ID>',
  },
  {
    command: '/org-analysis',
    label: '/org-analysis',
    description: 'Analyze org readiness by uploading a source/destination comparison JSON',
    placeholder: '/org-analysis',
  },
];


// Helper functions

function buildInitialDisplayedMessages(conversations: ChatConversation[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const conv of conversations) {
    for (const msg of conv.messages) {
      if (msg.role !== 'assistant') continue;
      map.set(`${conv.id}-${msg.id}`, msg.content);
      if (msg.rationale) {
        map.set(`${conv.id}-${msg.id}-rationale`, msg.rationale);
      }
    }
  }
  return map;
}

function buildInitialAnimatedKeys(conversations: ChatConversation[]): Set<string> {
  return new Set(
    conversations.flatMap((conv) =>
      conv.messages
        .filter((msg) => msg.role === 'assistant')
        .flatMap((msg) => {
          const key = `${conv.id}-${msg.id}`;
          return msg.rationale ? [key, `${conv.id}-${msg.id}-rationale`] : [key];
        }),
    ),
  );
}

function renderMarkdown(text: string, isUserMessage: boolean) {
  const textColor = isUserMessage ? 'white' : 'text.primary';

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: textColor,
              fontSize: '0.875rem',
              display: 'block',
              mb: 1,
              '&:last-child': { mb: 0 },
            }}
          >
            {children}
          </Typography>
        ),
        a: ({ href, children }) => (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: isUserMessage ? 'white' : 'primary.main', textDecoration: 'underline' }}
          >
            {children}
          </Link>
        ),
        strong: ({ children }) => <strong>{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        code: ({ children }) => (
          <Box
            component="code"
            sx={{
              bgcolor: isUserMessage ? 'rgba(0,0,0,0.2)' : 'grey.200',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.85em',
              fontFamily: 'monospace',
            }}
          >
            {children}
          </Box>
        ),
        pre: ({ children }) => (
          <Box
            component="pre"
            sx={{
              bgcolor: isUserMessage ? 'rgba(0,0,0,0.2)' : 'grey.200',
              p: 1.5,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.85em',
              fontFamily: 'monospace',
              my: 1,
            }}
          >
            {children}
          </Box>
        ),
        ul: ({ children }) => (
          <Box component="ul" sx={{ pl: 2, my: 1 }}>
            {children}
          </Box>
        ),
        ol: ({ children }) => (
          <Box component="ol" sx={{ pl: 2, my: 1 }}>
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Typography
            component="li"
            variant="body2"
            sx={{ color: textColor, fontSize: '0.875rem' }}
          >
            {children}
          </Typography>
        ),
        table: ({ children }) => (
          <Box
            sx={{
              overflowX: 'auto',
              my: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: isUserMessage ? 'rgba(255,255,255,0.3)' : 'divider',
            }}
          >
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.8125rem',
                lineHeight: 1.5,
              }}
            >
              {children}
            </Box>
          </Box>
        ),
        thead: ({ children }) => (
          <Box
            component="thead"
            sx={{
              bgcolor: isUserMessage ? 'rgba(0,0,0,0.25)' : 'grey.100',
            }}
          >
            {children}
          </Box>
        ),
        tbody: ({ children }) => <Box component="tbody">{children}</Box>,
        tr: ({ children }) => (
          <Box
            component="tr"
            sx={{
              borderBottom: '1px solid',
              borderColor: isUserMessage ? 'rgba(255,255,255,0.15)' : 'divider',
              '&:last-child': { borderBottom: 'none' },
              '&:nth-of-type(even)': {
                bgcolor: isUserMessage ? 'rgba(0,0,0,0.1)' : 'grey.50',
              },
            }}
          >
            {children}
          </Box>
        ),
        th: ({ children }) => (
          <Box
            component="th"
            sx={{
              px: 1.5,
              py: 0.75,
              textAlign: 'left',
              fontWeight: 700,
              fontSize: '0.8125rem',
              color: isUserMessage ? 'white' : 'text.primary',
              whiteSpace: 'nowrap',
              borderRight: '1px solid',
              borderColor: isUserMessage ? 'rgba(255,255,255,0.15)' : 'divider',
              '&:last-child': { borderRight: 'none' },
            }}
          >
            {children}
          </Box>
        ),
        td: ({ children }) => (
          <Box
            component="td"
            sx={{
              px: 1.5,
              py: 0.75,
              fontSize: '0.8125rem',
              color: isUserMessage ? 'white' : 'text.primary',
              verticalAlign: 'top',
              borderRight: '1px solid',
              borderColor: isUserMessage ? 'rgba(255,255,255,0.15)' : 'divider',
              '&:last-child': { borderRight: 'none' },
            }}
          >
            {children}
          </Box>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

function normalizeSources(sources?: string[]): string[] | undefined {
  if (!sources) return undefined;
  return [...new Set(sources.map((s) => s.replace(/\/$/, '')))];
}

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadMarkdown(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.md') ? filename : `${filename}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AIChat() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [initialJobSummary] = useState<{ chatId: string; transactionId: string; title: string } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('jobSummary') !== '1') return null;
    const routerState = window.history.state?.usr as { jobSummaryTransactionId?: string } | undefined;
    const transactionId = routerState?.jobSummaryTransactionId;
    if (!transactionId) return null;
    return {
      chatId: uuidv4(),
      transactionId,
      title: `Job Summary - ${transactionId}`.slice(0, TITLE_PREVIEW_LENGTH),
    };
  });

  const pendingJobSummaryRef = useRef<{ chatId: string; transactionId: string } | null>(initialJobSummary);

  const [conversations, setConversations] = useState<ChatConversation[]>(() => {
    let existing: ChatConversation[] = [];
    try {
      const saved = localStorage.getItem(AI_CHAT_STORAGE_KEY);
      existing = saved ? JSON.parse(saved) : [];
    } catch {
      existing = [];
    }

    if (initialJobSummary) {
      const newConversation: ChatConversation = {
        id: initialJobSummary.chatId,
        title: initialJobSummary.title,
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        transactionId: initialJobSummary.transactionId,
      };
      const trimmed = existing.length >= MAX_CONVERSATIONS ? existing.slice(0, MAX_CONVERSATIONS - 1) : existing;
      return [newConversation, ...trimmed];
    }

    return existing;
  });
  const [selectedChatId, setSelectedChatId] = useState<string | null>(() => {
    if (initialJobSummary) return initialJobSummary.chatId;
    try {
      return localStorage.getItem(AI_CHAT_SELECTED_KEY);
    } catch {
      return null;
    }
  });
  const [inputValue, setInputValue] = useState('');
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashMenuIndex, setSlashMenuIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [streamingMessageByChat, setStreamingMessageByChat] = useState<Map<string, string>>(new Map());
  const [streamingRationaleByChat, setStreamingRationaleByChat] = useState<Map<string, string>>(new Map());
  const [waitingForResponseByChat, setWaitingForResponseByChat] = useState<Map<string, boolean>>(new Map());
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [jobSummaryFetching, setJobSummaryFetching] = useState(!!initialJobSummary);
  const [jobErrorsDownloading, setJobErrorsDownloading] = useState(false);
  const [showRationaleGlobally, setShowRationaleGlobally] = useState(false);
  const [displayedMessages, setDisplayedMessages] = useState<Map<string, string>>(
    () => buildInitialDisplayedMessages(conversations),
  );
  const animatedMessagesRef = useRef<Set<string>>(buildInitialAnimatedKeys(conversations));
  const animationFrameRef = useRef<Map<string, number>>(new Map());
  const streamEndProcessedRef = useRef<Set<string>>(new Set());
  const rationaleChunksRef = useRef<Map<string, Set<string>>>(new Map());
  const streamingRationaleByChatRef = useRef<Map<string, string>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageCardRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const [downloadMenu, setDownloadMenu] = useState<{ anchorEl: HTMLElement; messageId: string } | null>(null);
  const [pngDownloading, setPngDownloading] = useState(false);
  const [orgAnalysisFile, setOrgAnalysisFile] = useState<File | null>(null);
  const [orgAnalysisJsonError, setOrgAnalysisJsonError] = useState<string | null>(null);
  const orgAnalysisFileRef = useRef<HTMLInputElement>(null);

  const selectedConversation = conversations.find((c) => c.id === selectedChatId);

  // ── Persistence ──

  useEffect(() => {
    try {
      localStorage.setItem(AI_CHAT_STORAGE_KEY, JSON.stringify(conversations));
    } catch {
      // Ignore storage errors
    }
  }, [conversations]);

  useEffect(() => {
    try {
      if (selectedChatId) {
        localStorage.setItem(AI_CHAT_SELECTED_KEY, selectedChatId);
      } else {
        localStorage.removeItem(AI_CHAT_SELECTED_KEY);
      }
    } catch {
      // Ignore storage errors
    }
  }, [selectedChatId]);

  // ── Animation ──

  const animateText = useCallback((messageId: string, fullText: string, onComplete?: () => void) => {
    if (fullText.length > SKIP_ANIMATION_THRESHOLD) {
      setDisplayedMessages((prev) => new Map(prev).set(messageId, fullText));
      onComplete?.();
      return;
    }
    const charsPerFrame = Math.max(CHARS_PER_FRAME_BASE, Math.ceil(fullText.length / 300));
    let currentIndex = 0;
    const animate = () => {
      if (currentIndex < fullText.length) {
        currentIndex = Math.min(currentIndex + charsPerFrame, fullText.length);
        setDisplayedMessages((prev) => new Map(prev).set(messageId, fullText.slice(0, currentIndex)));
        const frameId = requestAnimationFrame(animate);
        animationFrameRef.current.set(messageId, frameId);
      } else {
        animationFrameRef.current.delete(messageId);
        onComplete?.();
      }
    };
    const frameId = requestAnimationFrame(animate);
    animationFrameRef.current.set(messageId, frameId);
  }, []);

  useEffect(() => {
    if (!selectedConversation) return;

    selectedConversation.messages.forEach((msg) => {
      if (msg.role === 'assistant') {
        const messageKey = `${selectedConversation.id}-${msg.id}`;
        const rationaleKey = `${selectedConversation.id}-${msg.id}-rationale`;

        if (!animatedMessagesRef.current.has(messageKey)) {
          animatedMessagesRef.current.add(messageKey);
          animateText(messageKey, msg.content);
        }
        if (msg.rationale && !animatedMessagesRef.current.has(rationaleKey)) {
          animatedMessagesRef.current.add(rationaleKey);
          setDisplayedMessages((prev) => new Map(prev).set(rationaleKey, msg.rationale!));
        }
      }
    });

    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), SCROLL_DELAY_MS);
  }, [selectedConversation]);

  useEffect(() => {
    return () => {
      animationFrameRef.current.forEach((frameId) => cancelAnimationFrame(frameId));
      animationFrameRef.current.clear();
    };
  }, []);

  // ── WebSocket ──

  const handleWebSocketMessage = useCallback((data: { event_type: string; text?: string; chatId?: string; responseType?: string; sources?: string[] }) => {
    const chatId = data.chatId ?? '';
    if (data.event_type === 'text-generation') {
      if (data.responseType === 'rationale') {
        const chunkKey = `${chatId}-${data.text?.slice(0, DEDUP_KEY_LENGTH)}`;
        const chatChunks = rationaleChunksRef.current.get(chatId) || new Set();
        if (chatChunks.has(chunkKey)) return;
        chatChunks.add(chunkKey);
        rationaleChunksRef.current.set(chatId, chatChunks);
        const text = data.text ?? '';
        setStreamingRationaleByChat((prev) => {
          const next = new Map(prev);
          next.set(chatId, (next.get(chatId) ?? '') + text);
          streamingRationaleByChatRef.current = next;
          return next;
        });
      } else if (data.text) {
        setStreamingMessageByChat((prev) => {
          const next = new Map(prev);
          next.set(chatId, (next.get(chatId) ?? '') + data.text);
          return next;
        });
      }
    } else if (data.event_type === 'stream-end') {
      const streamKey = `${chatId}-${data.text?.slice(0, DEDUP_KEY_LENGTH)}`;
      if (streamEndProcessedRef.current.has(streamKey)) return;
      streamEndProcessedRef.current.add(streamKey);
      setTimeout(() => streamEndProcessedRef.current.delete(streamKey), DEDUP_WINDOW_MS);

      rationaleChunksRef.current.delete(chatId);
      const rationale = streamingRationaleByChatRef.current.get(chatId) ?? '';

      setStreamingMessageByChat((prev) => {
        const next = new Map(prev);
        next.delete(chatId);
        return next;
      });
      setStreamingRationaleByChat((prev) => {
        const next = new Map(prev);
        next.delete(chatId);
        streamingRationaleByChatRef.current = next;
        return next;
      });
      setWaitingForResponseByChat((prev) => {
        const next = new Map(prev);
        next.set(chatId, false);
        return next;
      });
      if (chatId) {
        const newMessageId = uuidv4();
        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === chatId) {
              const newMessage: ChatMessage = {
                id: newMessageId,
                role: 'assistant',
                content: data.text || '',
                timestamp: new Date().toLocaleTimeString(),
                chatId,
                sources: normalizeSources(data.sources),
                rationale: rationale || undefined,
                showRationale: true,
                showSources: false,
              };
              return {
                ...conv,
                messages: [...conv.messages, newMessage],
                updatedAt: new Date().toISOString(),
              };
            }
            return conv;
          }),
        );
      }
    }
  }, []);

  const { connections, connect, disconnect, sendMessage, onMessage } = useMultiWebSocket();

  useEffect(() => {
    return onMessage(handleWebSocketMessage);
  }, [handleWebSocketMessage, onMessage]);

  useEffect(() => {
    if (selectedChatId && !connections.get(selectedChatId)) {
      connect(selectedChatId);
    }
  }, [selectedChatId, connections, connect]);

  // ── Job Summary: fetch details then auto-send ──

  useEffect(() => {
    if (searchParams.get('jobSummary') === '1') {
      setSearchParams({}, { replace: true });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const pending = pendingJobSummaryRef.current;
    if (!pending) return;

    const connection = connections.get(pending.chatId);
    if (!connection?.isConnected) return;

    pendingJobSummaryRef.current = null;

    const fetchAndSend = async () => {
      try {
        const { prompt } = await fetchJobSummaryPrompt(pending.transactionId);
        const displayMessage = `Summarize BCP sync job: **${pending.transactionId}**`;

        const userMessage: ChatMessage = {
          id: uuidv4(),
          role: 'user',
          content: displayMessage,
          timestamp: new Date().toLocaleTimeString(),
          chatId: pending.chatId,
        };

        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === pending.chatId) {
              return {
                ...conv,
                messages: [...conv.messages, userMessage],
                updatedAt: new Date().toISOString(),
              };
            }
            return conv;
          }),
        );

        sendMessage(pending.chatId, {
          message: prompt,
          chatId: pending.chatId,
        });

        setWaitingForResponseByChat((prev) => new Map(prev).set(pending.chatId, true));
      } catch {
        const errorMessage: ChatMessage = {
          id: uuidv4(),
          role: 'assistant',
          content: `Failed to fetch job details for transaction **${pending.transactionId}**. Please try again or use the chat manually.`,
          timestamp: new Date().toLocaleTimeString(),
          chatId: pending.chatId,
        };

        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.id === pending.chatId) {
              return { ...conv, messages: [...conv.messages, errorMessage], updatedAt: new Date().toISOString() };
            }
            return conv;
          }),
        );
      } finally {
        setJobSummaryFetching(false);
      }
    };

    fetchAndSend();
  }, [connections, sendMessage]);

  // ── Handlers ──

  const ensureChatForInput = useCallback(() => {
    if (selectedChatId) return;
    if (conversations.length >= MAX_CONVERSATIONS) {
      setSelectedChatId(conversations[0].id);
      return;
    }
    const newConversation: ChatConversation = {
      id: uuidv4(),
      title: 'New conversation',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setConversations((prev) => [newConversation, ...prev]);
    setSelectedChatId(newConversation.id);
  }, [selectedChatId, conversations]);

  const handleNewChat = () => {
    if (conversations.length >= MAX_CONVERSATIONS) return;

    const newConversation: ChatConversation = {
      id: uuidv4(),
      title: 'New conversation',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setConversations([newConversation, ...conversations]);
    setSelectedChatId(newConversation.id);
  };

  const handleSelectChat = (chatId: string) => {
    setSelectedChatId(chatId);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), SCROLL_DELAY_MS);
  };

  const handleDeleteChat = (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    disconnect(chatId);
    setConversations((prev) => prev.filter((c) => c.id !== chatId));
    if (selectedChatId === chatId) {
      setSelectedChatId(null);
    }
  };

  const handleJobSummaryCommand = (transactionId: string) => {
    if (!selectedChatId) return;
    const connection = connections.get(selectedChatId);
    if (connection?.error || !connection?.isConnected) return;

    const displayMessage = `Summarize BCP sync job: **${transactionId}**`;
    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: displayMessage,
      timestamp: new Date().toLocaleTimeString(),
      chatId: selectedChatId,
    };

    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.id === selectedChatId) {
          return {
            ...conv,
            messages: [...conv.messages, userMessage],
            title: conv.messages.length === 0 ? displayMessage.slice(0, TITLE_PREVIEW_LENGTH) : conv.title,
            updatedAt: new Date().toISOString(),
            transactionId: transactionId,
          };
        }
        return conv;
      }),
    );

    setInputValue('');
    setJobSummaryFetching(true);

    const chatId = selectedChatId;
    (async () => {
      try {
        const { prompt } = await fetchJobSummaryPrompt(transactionId);

        sendMessage(chatId, { message: prompt, chatId });
        setWaitingForResponseByChat((prev) => new Map(prev).set(chatId, true));
      } catch {
        const errorMsg: ChatMessage = {
          id: uuidv4(),
          role: 'assistant',
          content: `Failed to fetch job details for transaction **${transactionId}**. Please check the transaction ID and try again.`,
          timestamp: new Date().toLocaleTimeString(),
          chatId,
        };
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === chatId
              ? { ...conv, messages: [...conv.messages, errorMsg], updatedAt: new Date().toISOString() }
              : conv,
          ),
        );
      } finally {
        setJobSummaryFetching(false);
      }
    })();
  };

  const handleOrgAnalysisFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrgAnalysisJsonError(null);
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      setOrgAnalysisJsonError('Please upload a .json file.');
      return;
    }
    setOrgAnalysisFile(file);
  };

  const handleOrgAnalysisCommand = async () => {
    if (!selectedChatId || !orgAnalysisFile) return;
    const connection = connections.get(selectedChatId);
    if (connection?.error || !connection?.isConnected) return;

    setOrgAnalysisJsonError(null);

    let rawJson: OrgComparisonRaw;
    try {
      const text = await orgAnalysisFile.text();
      rawJson = JSON.parse(text) as OrgComparisonRaw;
    } catch {
      setOrgAnalysisJsonError('Invalid JSON — could not parse the file.');
      return;
    }

    if (!rawJson.sourceOrg && !rawJson.destinationOrg) {
      setOrgAnalysisJsonError('JSON must contain a "sourceOrg" and/or "destinationOrg" key.');
      return;
    }
    if (rawJson.sourceOrg && !Array.isArray((rawJson.sourceOrg as OrgSnapshotRaw).licenses)) {
      setOrgAnalysisJsonError('sourceOrg.licenses array not found — check JSON structure.');
      return;
    }

    const diff = minimizeOrgComparisonJson(rawJson);
    const prompt = buildOrgAnalysisPrompt(diff);

    const srcId = diff.sourceId;
    const dstId = diff.destId;
    const flagCount = diff.limits.flags.length;
    const licGapCount = diff.licenses.srcOnly.length;
    const displayMessage = `Org readiness analysis: **${srcId}** → **${dstId}** | ${licGapCount} license gap(s), ${flagCount} limit flag(s) | from \`${orgAnalysisFile.name}\``;

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: displayMessage,
      timestamp: new Date().toLocaleTimeString(),
      chatId: selectedChatId,
    };

    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.id === selectedChatId) {
          return {
            ...conv,
            messages: [...conv.messages, userMessage],
            title: conv.messages.length === 0 ? displayMessage.slice(0, TITLE_PREVIEW_LENGTH) : conv.title,
            updatedAt: new Date().toISOString(),
          };
        }
        return conv;
      }),
    );

    setInputValue('');
    setOrgAnalysisFile(null);
    if (orgAnalysisFileRef.current) orgAnalysisFileRef.current.value = '';

    sendMessage(selectedChatId, { message: prompt, chatId: selectedChatId });
    setWaitingForResponseByChat((prev) => new Map(prev).set(selectedChatId, true));
  };

  const handleSend = () => {
    if (!inputValue.trim() || !selectedChatId) return;
    const selectedWaiting = waitingForResponseByChat.get(selectedChatId);
    const selectedStreaming = streamingMessageByChat.get(selectedChatId);
    if (selectedWaiting || selectedStreaming) return;
    const connection = connections.get(selectedChatId);
    if (connection?.error) return;

    const summaryMatch = inputValue.trim().match(SUMMARY_CMD_REGEX);
    if (summaryMatch) {
      handleJobSummaryCommand(summaryMatch[1]);
      return;
    }

    if (ORG_ANALYSIS_CMD_REGEX.test(inputValue.trim())) {
      if (orgAnalysisFile) {
        handleOrgAnalysisCommand();
      }
      return;
    }

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: inputValue,
      timestamp: new Date().toLocaleTimeString(),
      chatId: selectedChatId,
    };

    setConversations((prev) =>
      prev.map((conv) => {
        if (conv.id === selectedChatId) {
          return {
            ...conv,
            messages: [...conv.messages, userMessage],
            title: conv.messages.length === 0 ? inputValue.slice(0, TITLE_PREVIEW_LENGTH) : conv.title,
            updatedAt: new Date().toISOString(),
          };
        }
        return conv;
      }),
    );

    sendMessage(selectedChatId, {
      message: inputValue,
      chatId: selectedChatId,
    });

    setInputValue('');
    setWaitingForResponseByChat((prev) => new Map(prev).set(selectedChatId, true));
  };

  const toggleSources = (messageId: string) => {
    setConversations((prev) =>
      prev.map((conv) => ({
        ...conv,
        messages: conv.messages.map((msg) =>
          msg.id === messageId ? { ...msg, showSources: !msg.showSources } : msg,
        ),
      })),
    );
  };

  const toggleRationale = (messageId: string) => {
    setConversations((prev) =>
      prev.map((conv) => ({
        ...conv,
        messages: conv.messages.map((msg) =>
          msg.id === messageId ? { ...msg, showRationale: !msg.showRationale } : msg,
        ),
      })),
    );
  };

  const startEditTitle = (chatId: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingChatId(chatId);
    setEditingTitle(currentTitle);
  };

  const saveTitle = (chatId: string) => {
    if (editingTitle.trim()) {
      setConversations((prev) =>
        prev.map((conv) => (conv.id === chatId ? { ...conv, title: editingTitle.trim() } : conv)),
      );
    }
    setEditingChatId(null);
    setEditingTitle('');
  };

  const cancelEdit = () => {
    setEditingChatId(null);
    setEditingTitle('');
  };

  const handleDownloadChat = () => {
    if (!selectedConversation) return;

    const exportData = {
      chatId: selectedConversation.id,
      title: selectedConversation.title,
      createdAt: selectedConversation.createdAt,
      updatedAt: selectedConversation.updatedAt,
      messages: selectedConversation.messages.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        sources: msg.sources,
        rationale: msg.rationale,
      })),
    };

    const sanitizedTitle = selectedConversation.title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    downloadJson(`chat-${selectedConversation.id}-${sanitizedTitle}.json`, exportData);
  };

  const handleDownloadMessageMarkdown = (messageId: string) => {
    if (!selectedConversation) return;
    const message = selectedConversation.messages.find((m) => m.id === messageId);
    if (!message) return;
    const content =
      message.role === 'assistant'
        ? displayedMessages.get(`${selectedConversation.id}-${message.id}`) || message.content
        : message.content;
    const sanitized = selectedConversation.title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    downloadMarkdown(`message-${message.id}-${sanitized}`, content);
    setDownloadMenu(null);
  };

  const handleDownloadMessagePng = async (messageId: string) => {
    if (!selectedConversation) return;
    const el = messageCardRefs.current.get(messageId);
    if (!el) {
      setDownloadMenu(null);
      return;
    }
    setPngDownloading(true);
    try {
      const computedBg = typeof window !== 'undefined' ? window.getComputedStyle(el).backgroundColor : null;
      const backgroundColor =
        computedBg && computedBg !== 'rgba(0, 0, 0, 0)' && computedBg !== 'transparent'
          ? computedBg
          : '#fafafa';
      const dataUrl = await toPng(el, {
        cacheBust: true,
        backgroundColor,
        pixelRatio: Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2),
      });
      const sanitized = selectedConversation.title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `message-${messageId}-${sanitized}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setPngDownloading(false);
      setDownloadMenu(null);
    }
  };

  /**
   * Single download for both minified JSON files used to build the
   * job-summary AI prompt:
   *  - `<txn>-terraform-errors-minified.json`  ← `minify_tf_errors.py` equivalent
   *  - `<txn>-flow-errors-minified.json`       ← `aggregate_flow_errors.py` equivalent
   *
   * Always re-fetches the raw source files from S3 (so the network tab shows
   * the actual S3 GETs) and then runs the python-equivalent minify logic on
   * them. Saves both files unconditionally — an empty file is a valid signal
   * that there was nothing to extract.
   */
  const handleDownloadJobErrors = async () => {
    const txnId = selectedConversation?.transactionId;
    if (!txnId || !selectedChatId) return;
    setJobErrorsDownloading(true);
    try {
      const txn = await transactionService.getTransactionById(txnId);
      const region = txn.deploymentRegion ?? 'us-west-2';

      const [tfResult, flowResult] = await Promise.all([
        fetchTerraformErrors(txnId, region),
        fetchFlowErrors(txnId, region),
      ]);

      const terraformMinified = minifyTerraformErrorsByResourceType(tfResult.entries);
      const flowErrors = flowResult.minified;

      downloadJson(`${txnId}-terraform-errors-minified.json`, terraformMinified);
      downloadJson(`${txnId}-flow-errors-minified.json`, flowErrors);
    } finally {
      setJobErrorsDownloading(false);
    }
  };

  const isAtMaxChats = conversations.length >= MAX_CONVERSATIONS;
  const selectedStreamingMessage = selectedChatId ? (streamingMessageByChat.get(selectedChatId) ?? '') : '';
  const selectedStreamingRationale = selectedChatId ? (streamingRationaleByChat.get(selectedChatId) ?? '') : '';
  const selectedWaiting = selectedChatId ? (waitingForResponseByChat.get(selectedChatId) ?? false) : false;
  const isBusy = selectedWaiting || !!selectedStreamingMessage || jobSummaryFetching;
  const selectedConnection = selectedChatId ? connections.get(selectedChatId) : undefined;
  const isOrgAnalysisMode = ORG_ANALYSIS_CMD_REGEX.test(inputValue.trim());
  const canSend =
    !!inputValue.trim() &&
    !!selectedChatId &&
    !selectedConnection?.error &&
    !isBusy &&
    (!isOrgAnalysisMode || !!orgAnalysisFile);

  const filteredSlashCommands = useMemo(() => {
    const trimmed = inputValue.trimStart();
    if (!trimmed.startsWith('/')) return [];
    const typed = trimmed.split(/\s/)[0].toLowerCase();
    return SLASH_COMMANDS.filter((cmd) => cmd.command.startsWith(typed));
  }, [inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInputValue(val);

    const trimmed = val.trimStart();
    if (trimmed.startsWith('/') && !trimmed.includes(' ')) {
      setSlashMenuOpen(true);
      setSlashMenuIndex(0);
    } else {
      setSlashMenuOpen(false);
    }

    if (!ORG_ANALYSIS_CMD_REGEX.test(trimmed)) {
      if (orgAnalysisFile) {
        setOrgAnalysisFile(null);
        if (orgAnalysisFileRef.current) orgAnalysisFileRef.current.value = '';
      }
      if (orgAnalysisJsonError) setOrgAnalysisJsonError(null);
    }
  };

  const handleSlashSelect = (cmd: SlashCommand) => {
    setInputValue(cmd.command + ' ');
    setSlashMenuOpen(false);
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (slashMenuOpen && filteredSlashCommands.length > 0) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashMenuIndex((prev) => (prev > 0 ? prev - 1 : filteredSlashCommands.length - 1));
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashMenuIndex((prev) => (prev < filteredSlashCommands.length - 1 ? prev + 1 : 0));
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !inputValue.includes(' '))) {
        e.preventDefault();
        handleSlashSelect(filteredSlashCommands[slashMenuIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashMenuOpen(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Render ──

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 48px)', gap: 2 }}>
      {/* Sidebar */}
      <Paper
        elevation={0}
        sx={{
          width: 280,
          flexShrink: 0,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Tooltip title={isAtMaxChats ? `Maximum ${MAX_CONVERSATIONS} chats allowed` : 'Create new chat'}>
            <span>
              <Box
                onClick={handleNewChat}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  cursor: isAtMaxChats ? 'not-allowed' : 'pointer',
                  opacity: isAtMaxChats ? 0.5 : 1,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: isAtMaxChats ? 'divider' : 'primary.main',
                    bgcolor: isAtMaxChats ? 'transparent' : 'action.hover',
                  },
                }}
              >
                <AddIcon sx={{ color: 'primary.main' }} />
                <Typography fontWeight={500}>New Chat</Typography>
              </Box>
            </span>
          </Tooltip>
        </Box>

        <Divider />

        <List sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {conversations.map((chat) => (
            <ListItemButton
              key={chat.id}
              selected={chat.id === selectedChatId}
              onClick={() => handleSelectChat(chat.id)}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                '&.Mui-selected': { bgcolor: 'action.selected' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{ position: 'relative' }}>
                  <ChatIcon sx={{ fontSize: 18, color: chat.id === selectedChatId ? 'primary.main' : 'text.secondary' }} />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: connections.get(chat.id)?.isConnected ? 'success.main' : 'grey.400',
                      border: '1.5px solid',
                      borderColor: 'background.paper',
                    }}
                  />
                </Box>
              </ListItemIcon>
              {editingChatId === chat.id ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                  <TextField
                    size="small"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTitle(chat.id);
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                    sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 } }}
                  />
                  <IconButton size="small" onClick={() => saveTitle(chat.id)}>
                    <CheckIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton size="small" onClick={cancelEdit}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <ListItemText
                    primary={chat.title}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      startEditTitle(chat.id, chat.title, e as unknown as React.MouseEvent);
                    }}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: chat.id === selectedChatId ? 600 : 400,
                      noWrap: true,
                    }}
                  />
                  {chat.id === selectedChatId && (
                    <>
                      <Tooltip title="Edit chat title">
                        <IconButton size="small" onClick={(e) => startEditTitle(chat.id, chat.title, e)} sx={{ opacity: 0.6 }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download chat">
                        <IconButton size="small" onClick={handleDownloadChat} sx={{ opacity: 0.6 }}>
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete chat">
                        <IconButton size="small" onClick={(e) => handleDeleteChat(chat.id, e)} sx={{ opacity: 0.6 }}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            px: 3,
            py: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <Box sx={{ position: 'relative' }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                <BotIcon />
              </Avatar>
              {selectedChatId && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: selectedConnection?.isConnected && !selectedConnection?.error ? 'success.main' : 'error.main',
                    border: '2px solid',
                    borderColor: 'background.paper',
                  }}
                  title={selectedConnection?.error ? `Error: ${selectedConnection.error}` : selectedConnection?.isConnected ? 'Connected' : 'Disconnected'}
                />
              )}
            </Box>
            <Box>
              <Typography fontWeight={600}>AI Assistant</Typography>
              {selectedChatId && (
                <Typography variant="caption" color="text.secondary">
                  {selectedConnection?.error
                    ? `Error: ${selectedConnection.error}`
                    : selectedConnection?.isConnected
                    ? 'Connected'
                    : 'Disconnected'}
                </Typography>
              )}
            </Box>
          </Stack>
          <FormControlLabel
            control={<Switch checked={showRationaleGlobally} onChange={(e) => setShowRationaleGlobally(e.target.checked)} />}
            label="Show AI Thinking"
          />
        </Paper>

        {/* Messages */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {selectedConversation?.messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  flexDirection: message.role === 'user' ? 'row-reverse' : 'row',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: message.role === 'user' ? 'grey.300' : 'primary.main',
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                  }}
                >
                  {message.role === 'user' ? <PersonIcon /> : <BotIcon sx={{ fontSize: 20 }} />}
                </Avatar>
                <Box sx={{ maxWidth: '80%', minWidth: 0 }}>
                  {/* Collapsed rationale */}
                  {message.rationale && showRationaleGlobally && !message.showRationale && (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        bgcolor: 'info.lighter',
                        borderColor: 'info.main',
                        mb: 1,
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleRationale(message.id)}
                    >
                      <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LightbulbIcon sx={{ fontSize: 14, color: 'info.main' }} />
                            <Typography variant="caption" fontWeight={600} color="info.main">
                              AI Thinking (collapsed)
                            </Typography>
                          </Box>
                          <IconButton size="small" sx={{ opacity: 0.7 }}>
                            <ExpandMoreIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Expanded rationale */}
                  {message.rationale && message.showRationale && showRationaleGlobally && (
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        bgcolor: 'info.lighter',
                        borderColor: 'info.main',
                        mb: 1,
                      }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LightbulbIcon sx={{ fontSize: 16, color: 'info.main' }} />
                            <Typography variant="caption" fontWeight={600} color="info.main">
                              AI Thinking
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => toggleRationale(message.id)} sx={{ opacity: 0.7 }}>
                            <ExpandLessIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                          {displayedMessages.get(`${selectedConversation.id}-${message.id}-rationale`) || ''}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}

                  {/* Message content */}
                  <Card
                    variant="outlined"
                    ref={(node: HTMLDivElement | null) => {
                      if (node) messageCardRefs.current.set(message.id, node);
                      else messageCardRefs.current.delete(message.id);
                    }}
                    sx={{
                      borderRadius: 2,
                      bgcolor: message.role === 'user' ? 'primary.main' : 'grey.50',
                      borderColor: message.role === 'user' ? 'primary.main' : 'divider',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
                        {message.role === 'assistant'
                          ? renderMarkdown(displayedMessages.get(`${selectedConversation.id}-${message.id}`) || '', false)
                          : renderMarkdown(message.content, true)}
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Download (user) */}
                  {message.role === 'user' && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Tooltip title="Download message">
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            setDownloadMenu({ anchorEl: e.currentTarget, messageId: message.id })
                          }
                          sx={{ opacity: 0.7 }}
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}

                  {/* Sources + Download (assistant) */}
                  {message.role === 'assistant' && (
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      {message.sources && message.sources.length > 0 ? (
                        <Tooltip title={`View ${message.sources.length} source${message.sources.length > 1 ? 's' : ''}`}>
                          <IconButton size="small" onClick={() => toggleSources(message.id)} sx={{ opacity: 0.7 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SourceIcon sx={{ fontSize: 16 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {message.sources.length}
                              </Typography>
                            </Box>
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="No sources available for this response">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5 }}>
                            <SourceIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="caption" color="text.disabled">
                              No sources
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                      <Tooltip title="Download message">
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            setDownloadMenu({ anchorEl: e.currentTarget, messageId: message.id })
                          }
                          sx={{ opacity: 0.7 }}
                        >
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {selectedConversation.transactionId && (
                        <Tooltip title="Download both minified job-error JSON files (Terraform + Flow Publish)">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleDownloadJobErrors}
                              disabled={jobErrorsDownloading}
                              startIcon={
                                jobErrorsDownloading ? (
                                  <CircularProgress size={12} sx={{ color: 'error.main' }} />
                                ) : (
                                  <DescriptionIcon sx={{ fontSize: 14 }} />
                                )
                              }
                              sx={{
                                minHeight: 0,
                                py: 0.25,
                                px: 1,
                                fontSize: '0.75rem',
                                color: 'error.main',
                                borderColor: 'error.main',
                                '&:hover': {
                                  borderColor: 'error.dark',
                                  bgcolor: 'action.hover',
                                },
                                '& .MuiButton-startIcon': { mr: 0.5 },
                              }}
                            >
                              Download Job Errors
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  {message.sources && message.sources.length > 0 && message.showSources && (
                    <Collapse in={message.showSources}>
                      <Card variant="outlined" sx={{ mt: 1, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                            Sources ({message.sources.length})
                          </Typography>
                          {message.sources.map((source, idx) => (
                            <Link
                              key={idx}
                              href={source}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'block', fontSize: '0.75rem', mb: 0.5 }}
                            >
                              {source}
                            </Link>
                          ))}
                        </CardContent>
                      </Card>
                    </Collapse>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {message.timestamp}
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* Fetching job details indicator */}
            {jobSummaryFetching && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, flexShrink: 0 }}>
                  <BotIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ maxWidth: '80%', minWidth: 0 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50', borderColor: 'divider' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={16} thickness={5} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Fetching job details for summary...
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}

            {/* Streaming rationale */}
            {selectedStreamingRationale && showRationaleGlobally && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, flexShrink: 0 }}>
                  <BotIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ maxWidth: '80%', minWidth: 0 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'info.lighter', borderColor: 'info.main' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <LightbulbIcon sx={{ fontSize: 16, color: 'info.main' }} />
                        <Typography variant="caption" fontWeight={600} color="info.main">
                          AI Thinking...
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                        {selectedStreamingRationale}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}

            {/* Streaming message */}
            {selectedStreamingMessage && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, flexShrink: 0 }}>
                  <BotIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ maxWidth: '80%', minWidth: 0 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
                        {renderMarkdown(selectedStreamingMessage, false)}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}

            {/* Waiting indicator -- also show when rationale is streaming but the thinking toggle is off */}
            {selectedWaiting && !selectedStreamingMessage && (!selectedStreamingRationale || !showRationaleGlobally) && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, flexShrink: 0 }}>
                  <BotIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ maxWidth: '80%', minWidth: 0 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CircularProgress size={16} thickness={5} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          AI is thinking...
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
            <Menu
              anchorEl={downloadMenu?.anchorEl ?? null}
              open={Boolean(downloadMenu)}
              onClose={() => setDownloadMenu(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <MenuItem
                onClick={() => downloadMenu && handleDownloadMessageMarkdown(downloadMenu.messageId)}
              >
                <ListItemIcon>
                  <ArticleIcon fontSize="small" />
                </ListItemIcon>
                Markdown (.md)
              </MenuItem>
              <MenuItem
                onClick={() => downloadMenu && handleDownloadMessagePng(downloadMenu.messageId)}
                disabled={pngDownloading}
              >
                <ListItemIcon>
                  {pngDownloading ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <ImageIcon fontSize="small" />
                  )}
                </ListItemIcon>
                Image (.png)
              </MenuItem>
            </Menu>
          </Box>

          {/* Input */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50', position: 'relative' }}>
            {/* Slash command menu */}
            {slashMenuOpen && filteredSlashCommands.length > 0 && (
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 16,
                  right: 16,
                  mb: 0.5,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: 1,
                  borderColor: 'divider',
                  zIndex: 10,
                }}
              >
                <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Commands
                  </Typography>
                </Box>
                <List dense disablePadding>
                  {filteredSlashCommands.map((cmd, idx) => (
                    <ListItemButton
                      key={cmd.command}
                      selected={idx === slashMenuIndex}
                      onClick={() => handleSlashSelect(cmd)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        '&.Mui-selected': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {cmd.command === '/org-analysis' ? (
                          <CompareArrowsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        ) : (
                          <SummarizeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                              {cmd.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cmd.description}
                            </Typography>
                          </Stack>
                        }
                        secondary={
                          <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {cmd.placeholder}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}

            {/* Org analysis JSON upload zone */}
            {isOrgAnalysisMode && (
              <Paper
                variant="outlined"
                sx={{
                  mb: 1,
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: orgAnalysisFile ? 'success.main' : orgAnalysisJsonError ? 'error.main' : 'primary.light',
                  bgcolor: orgAnalysisFile ? 'success.lighter' : orgAnalysisJsonError ? 'error.lighter' : 'primary.lighter',
                }}
              >
                <input
                  ref={orgAnalysisFileRef}
                  type="file"
                  accept=".json,application/json"
                  style={{ display: 'none' }}
                  onChange={handleOrgAnalysisFileChange}
                />
                <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                  <CompareArrowsIcon
                    sx={{
                      color: orgAnalysisFile ? 'success.main' : orgAnalysisJsonError ? 'error.main' : 'primary.main',
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} color={orgAnalysisFile ? 'success.dark' : orgAnalysisJsonError ? 'error.dark' : 'primary.dark'}>
                      {orgAnalysisFile
                        ? `Ready: ${orgAnalysisFile.name}`
                        : orgAnalysisJsonError
                        ? orgAnalysisJsonError
                        : 'Upload source/destination org comparison JSON'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {orgAnalysisFile
                        ? 'Fields included: organizationId, features, limits, licenses, products. Excluded: integrationTypes, apnNumbers.'
                        : 'Expected format: { sourceOrg: {...}, destinationOrg: {...} }'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant={orgAnalysisFile ? 'outlined' : 'contained'}
                    startIcon={<UploadFileIcon />}
                    onClick={() => orgAnalysisFileRef.current?.click()}
                    sx={{ flexShrink: 0 }}
                  >
                    {orgAnalysisFile ? 'Change file' : 'Upload JSON'}
                  </Button>
                  {orgAnalysisFile && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setOrgAnalysisFile(null);
                        setOrgAnalysisJsonError(null);
                        if (orgAnalysisFileRef.current) orgAnalysisFileRef.current.value = '';
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Stack>
              </Paper>
            )}

            <TextField
              fullWidth
              multiline
              maxRows={4}
              inputRef={inputRef}
              placeholder={
                !selectedChatId
                  ? 'Type to start a new conversation...'
                  : isBusy
                  ? 'Waiting for AI response...'
                  : isOrgAnalysisMode
                  ? orgAnalysisFile
                    ? 'Press Enter or click send to run readiness analysis...'
                    : 'Upload a JSON file above, then press Enter to analyse...'
                  : 'Type a message or / for commands...'
              }
              value={inputValue}
              onChange={handleInputChange}
              onFocus={ensureChatForInput}
              onKeyDown={handleInputKeyDown}
              disabled={!!selectedConnection?.error || isBusy}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isBusy ? (
                      <CircularProgress size={20} sx={{ mr: 0.5 }} />
                    ) : (
                      <Tooltip title={selectedConnection?.error ? 'Connection error - cannot send messages' : 'Send message'}>
                        <span>
                          <IconButton
                            onClick={handleSend}
                            disabled={!canSend}
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: canSend ? 'text.primary' : 'grey.300',
                              color: canSend ? 'background.paper' : 'grey.500',
                              border: '1.5px solid',
                              borderColor: canSend ? 'text.primary' : 'grey.400',
                              '&:hover': { bgcolor: canSend ? 'grey.800' : 'grey.300' },
                              '&.Mui-disabled': { color: 'grey.500', borderColor: 'grey.400' },
                            }}
                          >
                            <SendIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: 'white',
                  pr: 1,
                },
              }}
            />
            <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5} sx={{ mt: 0.75, px: 0.5 }}>
              <WarningIcon sx={{ color: 'warning.main', fontSize: 14, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.3 }}>
                AI can make mistakes. Always verify information before communicating with customers. You are responsible for the accuracy of information provided.
              </Typography>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
